import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import RecentPostsSidebar from '../components/RecentPostsSidebar';

function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLetter, setSelectedLetter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  useEffect(() => {
    fetchBlogs();
  }, [selectedLetter, currentPage]);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 12
      };
      if (selectedLetter) params.letter = selectedLetter;

      const response = await axios.get('/api/blogs', { params });
      setBlogs(response.data.blogs);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleLetterClick = (letter) => {
    setSelectedLetter(selectedLetter === letter ? '' : letter);
    setCurrentPage(1);
  };

  if (loading && blogs.length === 0) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div
        className="container-fluid"
        style={{
          background: 'linear-gradient(rgba(var(--tgh-blue-rgb), 0.9), rgba(var(--tgh-blue-rgb), 0.9)), url(/img/hospital-building.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '4rem 0 3rem',
        }}
      >
        <div className="container">
          <div className="text-center">
            <h1 className="text-white mb-0" style={{ fontSize: '2.5rem', fontWeight: '600', fontFamily: "'Lora', 'Georgia', serif" }}>
              Health Articles
            </h1>
            <p className="text-white mt-3 mb-0" style={{ fontSize: '1.1rem', opacity: 0.9 }}>
              Expert health information and wellness tips
            </p>
            <nav aria-label="breadcrumb" className="mt-3 d-flex justify-content-center">
              <ol className="breadcrumb mb-0" style={{ backgroundColor: 'transparent', padding: 0 }}>
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">
                  Blog
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Blog Section */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa', minHeight: '60vh' }}>
        <div className="container">
          <div className="row tgh-page-with-sidebar">
            <div className="col-xl-9 col-lg-8 col-12">
              {/* Alphabet Filter */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="mb-3">
                    <label className="mb-2" style={{ fontSize: '0.95rem', fontWeight: '600', color: '#333', display: 'block' }}>
                      Find articles by first letter
                    </label>
                    <div className="d-flex flex-wrap alphabet-filter" style={{ gap: '0.5rem' }}>
                      {alphabet.map((letter) => (
                        <button
                          key={letter}
                          type="button"
                          className={`btn alphabet-btn ${selectedLetter === letter ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => handleLetterClick(letter)}
                        >
                          {letter}
                        </button>
                      ))}
                      <button
                        type="button"
                        className={`btn alphabet-btn ${selectedLetter === '#' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleLetterClick('#')}
                      >
                        #
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Blog Grid */}
              <div className="row g-4 tgh-card-grid">
                {blogs.length === 0 ? (
                  <div className="col-12 text-center py-5">
                    <p className="text-muted" style={{ fontSize: '1rem' }}>No blog articles found.</p>
                  </div>
                ) : (
                  blogs.map((blog) => (
                    <div key={blog.id} className="col-xl-4 col-lg-6 col-md-6 col-12">
                      <div className="card h-100 tgh-news-card" style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', transition: 'all 0.3s ease' }}>
                        <div className="tgh-card-media">
                          <img
                            src={blog.image ? (blog.image.startsWith('/uploads/') ? blog.image : `/uploads/${blog.image}`) : '/img/Gallery/opd1.jpg'}
                            className="card-img-top"
                            alt={blog.title}
                            onError={(e) => {
                              e.target.src = '/img/Gallery/opd1.jpg';
                            }}
                          />
                        </div>
                        <div className="card-body d-flex flex-column p-4">
                          {blog.category && (
                            <span className="badge bg-secondary mb-2" style={{ alignSelf: 'flex-start', fontSize: '0.75rem' }}>
                              {blog.category}
                            </span>
                          )}
                          <h5 className="card-title mb-3" style={{ fontSize: '1.15rem', fontWeight: '600', color: '#1a1a1a', lineHeight: '1.4' }}>
                            <Link to={`/blog/${blog.id}`} className="text-decoration-none" style={{ color: '#1a1a1a' }}>
                              {blog.title}
                            </Link>
                          </h5>
                          <p className="card-text text-muted mb-3" style={{ fontSize: '0.9rem', lineHeight: '1.5', textAlign: 'justify' }}>
                            {blog.excerpt || (blog.content ? blog.content.replace(/<[^>]*>/g, '').substring(0, 120) + '...' : '')}
                          </p>
                          <div className="mb-3" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <i className="bi bi-person"></i>
                              <span>{blog.author}</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <i className="bi bi-calendar3"></i>
                              <span>{formatDate(blog.created_at)}</span>
                            </div>
                          </div>
                          <Link
                            to={`/blog/${blog.id}`}
                            className="btn btn-primary mt-auto"
                            style={{
                              borderRadius: '4px',
                              padding: '0.6rem 1.5rem',
                              fontWeight: '500',
                              backgroundColor: 'var(--tgh-blue-light)',
                              borderColor: 'var(--tgh-blue-light)',
                              transition: 'all 0.2s'
                            }}
                          >
                            Read More <i className="bi bi-arrow-right ms-2"></i>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="row mt-5">
                  <div className="col-12">
                    <nav aria-label="Blog pagination">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                            Previous
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, i) => (
                          <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                              {i + 1}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
            </div>
            
            {/* Sidebar */}
            <div className="col-xl-3 col-lg-4 col-12 tgh-sidebar-col">
              <div style={{ position: 'sticky', top: '20px', zIndex: 10 }}>
                <RecentPostsSidebar type="blogs" limit={5} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Blog;
